import { Button, Stack, Typography, Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import DefentSheet from "@/features/HealthScore/components/DefentSheet";
import IconLoader from "@/features/Common/components/IconLoader";
import { convertPxToRem } from "@/utils";
import theme from "@/theme";

interface LifestyleFlyupModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConnect?: () => void;
  isButtonDisabled?: boolean;
  showAndroidSection?: boolean;
}

export default function LifestyleFlyupModal({
  isOpen,
  onClose,
  onConnect,
  isButtonDisabled,
  showAndroidSection,
}: LifestyleFlyupModalProps) {
  const { t } = useTranslation();

  const handlePrimaryButtonClick = () => {
    onConnect?.();
    onClose();
  };

  return (
    <DefentSheet
      expanded={isOpen}
      onClose={onClose}
      onExpandedChange={(expanded) => {
        if (!expanded) {
          onClose();
        }
      }}
    >
      <Stack pt={4} mb={0} pb={0}>
        <Stack spacing={2} alignItems="center" textAlign="center">
          <IconLoader
            icon="LifestyleIcon"
            color="primary"
            sx={{ fontSize: convertPxToRem(70) }}
          />
          <Stack spacing={2}>
            <Typography variant="h3" fontWeight={700} component="h2" px={2}>
              {t("LifestyleFlyupModal.title")}
            </Typography>
            <Typography variant="body1" color={theme.palette.neutral[700]}>
              {t("LifestyleFlyupModal.description")}
            </Typography>
          </Stack>
        </Stack>

        {showAndroidSection && (
          <Box
            mt={2}
            sx={{
              backgroundColor: theme.palette.neutral[100],
              borderRadius: 2,
              p: 2,
              textAlign: "left",
            }}
            data-testid="lifestyle-flyup-android-section"
          >
            <Stack direction="row" alignItems="center" gap={1} mb={1}>
              <IconLoader
                icon="AndroidIcon"
                sx={{ fontSize: convertPxToRem(24) }}
              />
              <Typography
                variant="subtitle1"
                fontWeight={700}
                color={theme.palette.neutral[900]}
              >
                {t("LifestyleFlyupModal.androidSection.title")}
              </Typography>
            </Stack>
            <Stack component="ul" sx={{ listStyle: "none" }} spacing={1}>
              <Typography
                variant="body2"
                component="li"
                color={theme.palette.neutral[900]}
              >
                {String(t("LifestyleFlyupModal.androidSection.android14"))
                  .split("<strong>")
                  .map((part: string, i: number) => {
                    if (i === 0) {
                      return <span key={i}>{part}</span>;
                    }
                    const [boldText, ...rest] = part.split("</strong>");
                    return (
                      <span key={i}>
                        <Typography
                          component="span"
                          fontWeight={600}
                          fontSize={convertPxToRem(12)}
                        >
                          {boldText}
                        </Typography>
                        {rest.join("</strong>")}
                      </span>
                    );
                  })}
              </Typography>
              <Typography
                variant="body2"
                component="li"
                color={theme.palette.neutral[900]}
              >
                {String(t("LifestyleFlyupModal.androidSection.android13"))
                  .split("<strong>")
                  .map((part: string, i: number) => {
                    if (i === 0) {
                      return <span key={i}>{part}</span>;
                    }
                    const [boldText, ...rest] = part.split("</strong>");
                    return (
                      <span key={i}>
                        <Typography
                          component="span"
                          fontWeight={600}
                          fontSize={convertPxToRem(12)}
                        >
                          {boldText}
                        </Typography>
                        {rest.join("</strong>")}
                      </span>
                    );
                  })}
              </Typography>
            </Stack>
          </Box>
        )}

        <Button
          variant="contained"
          color="primary"
          fullWidth
          sx={{ mt: 4 }}
          disabled={isButtonDisabled}
          onClick={handlePrimaryButtonClick}
        >
          {t("LifestyleFlyupModal.cta")}
        </Button>

        {showAndroidSection && (
          <Box
            sx={{
              backgroundColor: theme.palette.secondary[100],
              borderRadius: 2,
              p: 2,
              display: "flex",
              alignItems: "flex-start",
              gap: 1,
              mt: 4,
            }}
            data-testid="lifestyle-flyup-support-message"
          >
            <IconLoader
              icon="FlagIcon"
              sx={{
                fontSize: convertPxToRem(20),
                color: theme.palette.secondary[900],
              }}
            />
            <Typography variant="body2" color={theme.palette.neutral[900]}>
              {t("LifestyleFlyupModal.supportMessage")}
            </Typography>
          </Box>
        )}
      </Stack>
    </DefentSheet>
  );
}
